import { useContext } from 'react'

import {
  useMediaAnalysisAutomationListApi,
  useUpsertMediaAnalysisAutomationApi
} from '@/common/api/modules/mediaAnalysisAutomation'
import { ScheduleItem } from '@/common/api/params/mediaAnalysisAutomation'
import { useSiteId } from '@/common/app/site'
import { dateTimeStr } from '@/common/form/date'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import CheckBox, { CheckBoxType } from '@/components/view/atoms/checkbox/CheckBox'
import Heading from '@/components/view/atoms/heading/Heading'
import SimpleTable from '@/components/view/atoms/table/SimpleTable'
import TableRow from '@/components/view/atoms/table/TableRow'
import TableRowItem from '@/components/view/atoms/table/TableRowItem'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import ItemAlign from '@/components/view/layouts/item/ItemAlign'
import Content from '@/components/view/layouts/page/Content'
import Main, { MainLayoutType } from '@/components/view/layouts/page/Main'
import PageTitle from '@/components/view/molecules/title/PageTitle'

import DetailPageLoader from '@/components/container/DetailPageLoader'
import AppTitleBadge, { AppTitleBadgeType } from '@/components/container/layout/AppTitleBadge'
import MediaAnalysisAutomationSettingsModalProvider, {
  MediaAnalysisAutomationSettingsModalContext
} from '@/components/container/modal/mediaAnalysisAutomation/MediaAnalysisAutomationSettingsModalProvider'
import { ToastMessageContext } from '@/components/container/utils/message/ToastMessageProvider'

const ScheduleSection = ({
  siteId,
  title,
  items,
  onRefresh
}: {
  siteId: number
  title: string
  items: ScheduleItem[]
  onRefresh: () => void
}) => {
  const { addMessage } = useContext(ToastMessageContext)
  const { showModal } = useContext(MediaAnalysisAutomationSettingsModalContext)
  const { trigger: upsert } = useUpsertMediaAnalysisAutomationApi({
    onSuccess: () => {
      addMessage({ type: 'success', text: '自動実行設定をOFFにしました' })
      onRefresh()
    }
  })

  if (items.length === 0) return null

  const openSettings = (item: ScheduleItem, forEnabling: boolean) =>
    showModal({ siteId, item, onSaved: onRefresh, forEnabling })

  const handleToggle = (item: ScheduleItem, checked: boolean) => {
    if (checked) {
      openSettings(item, true)
    } else {
      upsert({ site_id: siteId, analysis_type: item.key, enabled: false })
    }
  }

  return (
    <AreaContent>
      <SimpleTable>
        <thead>
          <TableRow head>
            <TableRowItem>
              <Text size={12}>{title}</Text>
            </TableRowItem>
            <TableRowItem width={160}>
              <Text size={12}>自動実行</Text>
            </TableRowItem>
            <TableRowItem width={200}>
              <Text size={12}>頻度</Text>
            </TableRowItem>
            <TableRowItem width={160}>
              <Text size={12}>次回実行</Text>
            </TableRowItem>
          </TableRow>
        </thead>
        <tbody>
          {items.map((item) => (
            <TableRow key={item.key}>
              <TableRowItem>
                <Text>{item.label}</Text>
              </TableRowItem>
              <TableRowItem width={160}>
                <CheckBox
                  name={`schedule-${item.key}`}
                  type={CheckBoxType.TOGGLE_TEXT}
                  checked={item.enabled}
                  onChange={(v) => handleToggle(item, v)}
                  toggleText={{ left: 'ON', right: 'OFF' }}
                />
              </TableRowItem>
              <TableRowItem width={200}>
                <ItemAlign direction='horizon' align='center' justify='between' gapX={8}>
                  <Text size={12} color={item.enabled ? TextColor.PRIMARY : TextColor.SECONDARY}>
                    {item.frequency_label ?? '-'}
                  </Text>
                  <Button onClick={() => openSettings(item, false)} color={ButtonColor.SECONDARY}>
                    編集
                  </Button>
                </ItemAlign>
              </TableRowItem>
              <TableRowItem width={160}>
                <Text size={12}>{item.next_run_at ? dateTimeStr(item.next_run_at) : '-'}</Text>
              </TableRowItem>
            </TableRow>
          ))}
        </tbody>
      </SimpleTable>
    </AreaContent>
  )
}

const MediaAnalysisAutomationPageInner = () => {
  const siteId = useSiteId()
  const { data, error, mutate } = useMediaAnalysisAutomationListApi({ site_id: siteId })
  const items = data?.items ?? []
  const seoItems = items.filter((i) => !i.is_geo_llmo)
  const geoLlmoItems = items.filter((i) => i.is_geo_llmo)

  return (
    <DetailPageLoader error={error} data={data} small>
      <Main type={MainLayoutType.SMALL}>
        <PageTitle
          title='分析自動実行'
          description='各分析の自動実行スケジュールと、推奨アクションによる記事更新の自動化を設定します。'
          badgeNode={<AppTitleBadge type={AppTitleBadgeType.MEDIA} />}
        />
        <Content>
          <Area>
            <Heading subText='「ON」をクリックすると、頻度など自動実行の設定モーダルが開きます（OFF→ON時は設定必須。ON中の項目は設定変更として再度開けます）。'>
              分析の自動実行
            </Heading>
            <ScheduleSection
              siteId={siteId}
              title='SEO分析'
              items={seoItems}
              onRefresh={() => mutate()}
            />
            <ScheduleSection
              siteId={siteId}
              title='GEO/LLMO分析'
              items={geoLlmoItems}
              onRefresh={() => mutate()}
            />
          </Area>
        </Content>
      </Main>
    </DetailPageLoader>
  )
}

const MediaAnalysisAutomationPage = () => (
  <MediaAnalysisAutomationSettingsModalProvider>
    <MediaAnalysisAutomationPageInner />
  </MediaAnalysisAutomationSettingsModalProvider>
)

export default MediaAnalysisAutomationPage
